<template>
  <div class="mobile-confirm-section">
    <div class="mobile-confirm-header">
      <h3 class="mobile-confirm-title">3. Confirm your entries</h3>
    </div>
    
    <div v-if="lines.length === 0" class="mobile-no-entries">
      No entries selected
    </div>
    
    <div v-else class="mobile-lines-section">
      <div class="mobile-lines-header">
        <span>Selected Lines ({{ lines.length }})</span>
        <button class="mobile-clear-btn" @click="clearAll">Clear All</button>
      </div>
      
      <div class="mobile-lines-list">
        <div
          v-for="(line, index) in lines"
          :key="index"
          class="mobile-line-item"
        >
          <div class="mobile-line-content">
            <div class="mobile-line-numbers">
              <div class="mobile-line-balls">
                <div
                  v-for="number in line.numbers"
                  :key="number"
                  class="mobile-line-ball"
                >
                  {{ number }}
                </div>
                <div v-if="line.bonus" class="mobile-line-bonus">
                  <span class="mobile-line-bonus-separator">+</span>
                  <div class="mobile-line-bonus-ball">
                    {{ line.bonus }}
                  </div>
                </div>
              </div>
            </div>
                <div class="mobile-line-odds">
                  {{ getLineOdds(line) }}
                </div>
          </div>
          <button
            class="mobile-remove-line-btn"
            @click="removeLine(index)"
          >
            ×
          </button>
        </div>
      </div>
      
      <button
        class="mobile-add-to-betslip-btn"
        @click="addToBetslip"
        :disabled="lines.length === 0 || isProcessing"
      >
        Add to Betslip
      </button>
    </div>
  </div>
</template>

<script setup lang="ts">
import { defineProps, defineEmits, withDefaults } from 'vue';
import { formatOdds } from '../../utils/currency';

interface MarketOutcome {
  outcome_id: number;
  text: string;
  select_numbers: boolean;
  pick: number;
  slug: string;
  odds: string;
}

interface Market {
  market_id: number;
  market_name: string;
  outcomes: MarketOutcome[];
}

interface Line {
  numbers: number[];
  bonus?: number;
  market?: {
    marketGroup?: {
      slug: string;
    };
    market: Market;
  };
}

interface Props {
  lines: Line[];
  currentGameSlug?: string;
  selectedRelatedGames?: string[];
  isProcessing?: boolean;
}

withDefaults(defineProps<Props>(), {
  selectedRelatedGames: () => [],
  isProcessing: false,
});

const emit = defineEmits<{
  'clear-all': [];
  'remove-line': [index: number];
  'add-to-betslip': [];
  'update:selectedRelatedGames': [games: string[]];
}>();

const clearAll = () => {
  emit('clear-all');
};

const removeLine = (index: number) => {
  emit('remove-line', index);
};

const addToBetslip = () => {
  emit('add-to-betslip');
};

const getLineOdds = (line: Line): string => {
  if (!line.market?.market.outcomes) {
    return '1.00';
  }
  
  const numberCount = line.numbers.length;
  const outcome = line.market.market.outcomes.find(
    (outcome: MarketOutcome) => outcome.select_numbers && outcome.pick === numberCount
  );
  
  return outcome ? formatOdds(Number(outcome.odds)) : '1.00';
};
</script>

<script lang="ts">
export default {
  name: "MobileConfirmSection",
};
</script>

<style scoped>
/* Step 3: Confirm your entries */
.mobile-confirm-section {
  background: var(--bg-secondary);
  border-radius: 12px;
  margin-top: 16px;
  overflow: hidden;
}

.mobile-confirm-header {
  padding: 16px 20px;
  background: var(--bg-tertiary);
  border-bottom: 1px solid var(--border-color);
}

.mobile-confirm-title {
  font-size: 16px;
  font-weight: 600;
  color: var(--text-primary);
  margin: 0;
}

.mobile-no-entries {
  padding: 20px;
  text-align: center;
  font-size: 14px;
  color: var(--text-secondary);
  font-style: italic;
}

.mobile-lines-section {
  background: transparent;
  border-radius: 0;
  padding: 16px 20px;
}

.mobile-lines-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 8px;
}

.mobile-lines-header span {
  font-size: 14px;
  font-weight: 600;
  color: var(--text-primary);
}

.mobile-clear-btn {
  background: none;
  border: none;
  color: var(--accent-color);
  font-size: 12px;
  font-weight: 600;
  cursor: pointer;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  transition: opacity 0.2s ease;
}

.mobile-clear-btn:hover {
  opacity: 0.8;
}

.mobile-lines-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 16px;
}

.mobile-line-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  background: var(--bg-primary);
  border: 1px solid var(--border-color);
  border-radius: 8px;
  transition: all 0.2s ease;
}

.mobile-line-item:hover {
  border-color: var(--accent-color);
}

.mobile-line-content {
  flex: 1;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.mobile-line-numbers {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.mobile-line-balls {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}

.mobile-line-ball {
  width: 30px;
  height: 30px;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--accent-color) 0%, #d97706 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 13px;
  font-weight: 700;
  border: 2px solid var(--accent-color);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.15);
}

.mobile-line-bonus {
  display: flex;
  align-items: center;
  gap: 6px;
}

.mobile-line-bonus-separator {
  font-size: 14px;
  font-weight: 600;
  color: var(--text-secondary);
  margin-right: 2px;
}

.mobile-line-bonus-ball {
  width: 26px;
  height: 26px;
  border-radius: 50%;
  background: linear-gradient(135deg, var(--text-ball-selected) 0%, #f8f9fa 100%);
  color: var(--accent-color);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 12px;
  font-weight: 700;
  border: 2px solid var(--accent-color);
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
}

.mobile-line-odds {
  font-size: 14px;
  font-weight: 600;
  color: var(--accent-color);
}

.mobile-remove-line-btn {
  background: var(--bg-danger);
  border: none;
  color: white;
  width: 24px;
  height: 24px;
  border-radius: 50%;
  font-size: 16px;
  font-weight: bold;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: 12px;
  transition: all 0.2s ease;
}

.mobile-remove-line-btn:hover {
  background: var(--bg-danger-hover);
  transform: scale(1.1);
}

.mobile-add-to-betslip-btn {
  width: 100%;
  padding: 16px;
  background: var(--accent-color);
  color: white;
  border: none;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  text-transform: uppercase;
  transition: all 0.2s ease;
}

.mobile-add-to-betslip-btn:hover:not(:disabled) {
  background: var(--accent-color-dark, #e04500);
}

.mobile-add-to-betslip-btn:disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

/* Dark mode adjustments */
@media (prefers-color-scheme: dark) {
  .mobile-confirm-section {
    background: var(--bg-secondary);
  }
  
  .mobile-confirm-header {
    background: var(--bg-tertiary);
  }
  
  .mobile-line-item {
    background: var(--bg-primary);
  }
}

/* Responsive adjustments */
@media (max-width: 480px) {
  .mobile-confirm-header {
    padding: 12px 16px;
  }
  
  .mobile-confirm-title {
    font-size: 14px;
  }
  
  .mobile-no-entries {
    padding: 16px;
    font-size: 13px;
  }
  
  .mobile-lines-section {
    padding: 12px 8px;
  }
  
  .mobile-line-item {
    padding: 10px 12px;
  }
  
  .mobile-line-ball {
    width: 26px;
    height: 26px;
    font-size: 12px;
  }

  .mobile-line-bonus-ball {
    width: 22px;
    height: 22px;
    font-size: 11px;
  }
  
  .mobile-line-bonus-separator {
    font-size: 12px;
  }
  
  .mobile-line-balls {
    gap: 4px;
  }
  
  .mobile-line-odds {
    font-size: 13px;
  }
  
  .mobile-add-to-betslip-btn {
    padding: 14px;
    font-size: 14px;
  }
}
</style>


